/**
 * Real-time WebSocket Types
 *
 * Type definitions for WebSocket communication based on realtime-events.md spec.
 */

// Connection states
export type ConnectionState = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

// Room types
export type RoomType = 'board' | 'item' | 'user' | 'workspace';

// Base message structure
export interface BaseMessage {
  type: string;
  timestamp?: string;
}

// Client -> Server messages
export interface AuthMessage extends BaseMessage {
  type: 'auth';
  payload: {
    token: string;
  };
}

export interface SubscribeMessage extends BaseMessage {
  type: 'subscribe';
  room: string;
}

export interface UnsubscribeMessage extends BaseMessage {
  type: 'unsubscribe';
  room: string;
}

export interface PingMessage extends BaseMessage {
  type: 'ping';
}

export interface SyncMessage extends BaseMessage {
  type: 'sync';
  room: string;
  since_sequence: number;
}

export interface PresenceUpdateMessage extends BaseMessage {
  type: 'presence.update';
  room: string;
  payload: PresenceData;
}

export interface CursorSelectMessage extends BaseMessage {
  type: 'cursor.select';
  room: string;
  payload: {
    item_id?: string;
    column_id?: string;
    cell_range?: { start: string; end: string };
  };
}

export interface TypingMessage extends BaseMessage {
  type: 'typing.start' | 'typing.stop';
  room: string;
  payload: {
    update_id?: string;
    item_id?: string;
  };
}

export type ClientMessage =
  | AuthMessage
  | SubscribeMessage
  | UnsubscribeMessage
  | PingMessage
  | SyncMessage
  | PresenceUpdateMessage
  | CursorSelectMessage
  | TypingMessage;

// Server -> Client messages
export interface ConnectedMessage extends BaseMessage {
  type: 'connected';
  payload: {
    connection_id: string;
    user_id: string;
  };
}

export interface SubscribedMessage extends BaseMessage {
  type: 'subscribed';
  room: string;
}

export interface UnsubscribedMessage extends BaseMessage {
  type: 'unsubscribed';
  room: string;
}

export interface PongMessage extends BaseMessage {
  type: 'pong';
}

export interface ErrorMessage extends BaseMessage {
  type: 'error';
  error: {
    code: number;
    message: string;
    retry_after?: number;
  };
}

export interface PresenceStateMessage extends BaseMessage {
  type: 'presence_state';
  room: string;
  payload: {
    users: PresenceData[];
  };
}

export interface SyncResponseMessage extends BaseMessage {
  type: 'sync_response';
  room: string;
  events: RealtimeEvent[];
  has_more: boolean;
  error?: 'sequence_too_old';
}

// Event message
export interface EventMessage extends BaseMessage {
  type: 'event';
  event: string;
  room: string;
  payload: unknown;
  sequence: number;
}

export type ServerMessage =
  | ConnectedMessage
  | SubscribedMessage
  | UnsubscribedMessage
  | PongMessage
  | ErrorMessage
  | PresenceStateMessage
  | SyncResponseMessage
  | EventMessage;

// Presence data
export interface PresenceData {
  user_id: string;
  name: string;
  avatar_url?: string;
  status: 'online' | 'away' | 'busy';
  cursor?: {
    x: number;
    y: number;
    item_id?: string;
    column_id?: string;
  };
  viewing?: {
    board_id?: string;
    item_id?: string;
  };
  last_seen: string;
}

// Event types
export type BoardEventType =
  | 'board.updated'
  | 'group.created'
  | 'group.updated'
  | 'group.deleted'
  | 'group.reordered'
  | 'group.collapsed'
  | 'column.created'
  | 'column.updated'
  | 'column.deleted'
  | 'column.reordered'
  | 'column.resized'
  | 'item.created'
  | 'item.updated'
  | 'item.deleted'
  | 'item.reordered'
  | 'item.moved'
  | 'column_value.changed';

export type ItemEventType =
  | 'update.created'
  | 'update.edited'
  | 'update.deleted'
  | 'update.liked'
  | 'reply.created'
  | 'reply.edited'
  | 'reply.deleted'
  | 'file.attached'
  | 'file.removed'
  | 'subitem.created'
  | 'subitem.updated'
  | 'subitem.deleted'
  | 'subitem.reordered';

export type UserEventType =
  | 'notification.new'
  | 'notification.read'
  | 'notification.dismissed'
  | 'mention.new';

export type WorkspaceEventType =
  | 'member.joined'
  | 'member.left'
  | 'member.role_changed'
  | 'board.created'
  | 'board.deleted'
  | 'file.uploaded'
  | 'file.versioned'
  | 'file.scanned';

export type PresenceEventType =
  | 'presence.join'
  | 'presence.leave'
  | 'presence.update'
  | 'cursor.move'
  | 'cursor.select'
  | 'typing.start'
  | 'typing.stop';

export type RealtimeEventType =
  | BoardEventType
  | ItemEventType
  | UserEventType
  | WorkspaceEventType
  | PresenceEventType;

// Event payloads
export interface BoardUpdatedPayload {
  board_id: string;
  changes: {
    name?: string;
    description?: string;
    visibility?: string;
  };
  updated_by: string;
}

export interface GroupCreatedPayload {
  group: {
    id: string;
    name: string;
    color: string;
    position: number;
  };
  created_by: string;
}

export interface GroupUpdatedPayload {
  group_id: string;
  changes: {
    name?: string;
    color?: string;
  };
  updated_by: string;
}

export interface GroupDeletedPayload {
  group_id: string;
  deleted_by: string;
}

export interface GroupReorderedPayload {
  group_id: string;
  old_position: number;
  new_position: number;
  updated_by: string;
}

export interface ColumnCreatedPayload {
  column: {
    id: string;
    name: string;
    type: string;
    width: number;
    position: number;
    settings?: Record<string, unknown>;
  };
  created_by: string;
}

export interface ColumnUpdatedPayload {
  column_id: string;
  changes: {
    name?: string;
    width?: number;
    settings?: Record<string, unknown>;
  };
  updated_by: string;
}

export interface ColumnDeletedPayload {
  column_id: string;
  deleted_by: string;
}

export interface ColumnReorderedPayload {
  column_id: string;
  old_position: number;
  new_position: number;
  updated_by: string;
}

export interface ItemCreatedPayload {
  item: {
    id: string;
    name: string;
    group_id: string;
    position: number;
    values: Record<string, unknown>;
  };
  created_by: string;
}

export interface ItemUpdatedPayload {
  item_id: string;
  group_id: string;
  changes: {
    name?: string;
  };
  updated_by: string;
}

export interface ItemDeletedPayload {
  item_id: string;
  group_id: string;
  deleted_by: string;
}

export interface ItemReorderedPayload {
  item_id: string;
  group_id: string;
  old_position: number;
  new_position: number;
  updated_by: string;
}

export interface ItemMovedPayload {
  item_id: string;
  from_group_id: string;
  to_group_id: string;
  position: number;
  moved_by: string;
}

export interface ColumnValueChangedPayload {
  item_id: string;
  column_id: string;
  value: unknown;
  previous_value: unknown;
  updated_by: string;
}

export interface RealtimeEvent {
  event: RealtimeEventType;
  room: string;
  payload: unknown;
  sequence: number;
  timestamp: string;
}

// Error codes
export const ERROR_CODES = {
  UNAUTHORIZED: 1001,
  ROOM_NOT_FOUND: 1002,
  PERMISSION_DENIED: 1003,
  RATE_LIMITED: 1004,
  INVALID_MESSAGE: 1005,
} as const;

// Configuration
export interface RealtimeConfig {
  url: string;
  token: string;
  reconnectBaseDelay?: number;
  reconnectMaxDelay?: number;
  pingInterval?: number;
  connectionTimeout?: number;
}

export const DEFAULT_CONFIG = {
  reconnectBaseDelay: 1000,
  reconnectMaxDelay: 30000,
  pingInterval: 30000,
  connectionTimeout: 10000,
} as const;
